import { Button, Checkbox, Drawer, DrawerProps, Space } from "antd"
import { IExtEvent, useCreatePreassignation, useExtEvents } from "../queries"
import { useTranslation } from "react-i18next"
import { FormControl } from "@atoms"
import Loader from "components/loader"
import { formatDay } from "utils/format-datetime"
import { usePreassignationForm } from "./use-form"
import { IPreassignationForm } from "../types"
import { useEffect } from "react"
import { Controller } from "react-hook-form"
import { useQueryClient } from "@tanstack/react-query"

interface Props extends DrawerProps {
  onClose: () => void
  isOpen: boolean
  data?: IExtEvent
}

const CreatePreassignationDrawer: React.FC<Props> = ({ data, ...props }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const createPreassignation = useCreatePreassignation({
    meta: {
      type: "create",
      entity: t("assignations.preassignation_one"),
      context: "female",
      count: 1,
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<IExtEvent[]>(
        [
          "operation",
          "production",
          "events",
          "external",
          {
            filter: {
              session: { values: [data?.session?.id], operator: "in" },
              activity: { values: [data?.activity_id], operator: "in" },
              status: { values: ["draft", "approved"], operator: "in" },
            },
            check_preassignation_for_employee: "user",
          },
        ],
        (queryData) =>
          queryData?.map((el) =>
            variables.events.includes(el.id)
              ? { ...el, requested_employee_is_already_pre_assigned: true }
              : el
          )
      )
      queryClient.setQueryData<IExtEvent[]>(
        [
          "operation",
          "production",
          "events",
          "external",
          {
            filter: {
              session: { values: [data?.session?.id], operator: "in" },
              status: { values: ["draft", "approved"], operator: "in" },
            },
            check_preassignation_for_employee: "user",
          },
        ],
        (queryData) =>
          queryData?.map((el) =>
            variables.events.includes(el.id)
              ? { ...el, requested_employee_is_already_pre_assigned: true }
              : el
          )
      )
    },
  })

  const events = useExtEvents(
    {
      filter: {
        session: { values: [data?.session?.id], operator: "in" },
        activity: { values: [data?.activity_id], operator: "in" },
        status: { values: ["draft", "approved"], operator: "in" },
      },
      check_preassignation_for_employee: "user",
    },
    {
      enabled: !!data?.session?.id && props.isOpen,
    }
  )

  const {
    reset,
    control,
    setValue,
    setError,
    handleSubmit,
    formState: { errors },
  } = usePreassignationForm({ events: [] })

  const onSubmit = (data: IPreassignationForm) => {
    createPreassignation.mutate(data, {
      onSuccess() {
        props.onClose()
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    })
  }

  useEffect(() => {
    !props.open && reset()
  }, [props.open, reset])

  useEffect(() => {
    if (events.data && data) {
      const preassignedEvents = events.data
        .filter((event) => event.requested_employee_is_already_pre_assigned)
        .map((event) => event.id)

      setValue(
        "events",
        preassignedEvents.includes(data.id)
          ? preassignedEvents
          : [data.id, ...preassignedEvents]
      )
    }
  }, [events.data, setValue, data])

  return (
    <Drawer
      title={t("global.new", {
        entity: t("assignations.preassignation_one"),
        context: "female",
        count: 1,
      })}
      {...props}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl>
          <div>
            <span className="subheader-heavy">{t("labels.session")} : </span>
            <span className="subheader">{data?.session?.name}</span>
          </div>
          <div style={{ marginBottom: "var(--size-4)" }}>
            <span className="subheader-heavy">{t("labels.activity")} : </span>
            <span className="subheader">{data?.name}</span>
          </div>
          <h3 className="body-heavy" style={{ marginBottom: "var(--size-2)" }}>
            {t("events.similar_events")}
          </h3>
          {events.isLoading ? (
            <Loader />
          ) : (
            <Controller
              name="events"
              control={control}
              render={({ field }) => {
                const options = events.data?.map((event) => ({
                  value: event.id,
                  label: `${formatDay(event.start_date)} ,${
                    event.start_hour
                  }h - ${event.end_hour}h`,
                  disabled: event.requested_employee_is_already_pre_assigned,
                }))
                return (
                  <>
                    <Checkbox
                      checked={field.value.length === events.data?.length}
                      onChange={(e) => {
                        field.onChange(
                          e.target.checked
                            ? events.data?.map((event) => event.id)
                            : events.data
                                ?.filter(
                                  (event) =>
                                    event.requested_employee_is_already_pre_assigned
                                )
                                .map((event) => event.id)
                        )
                      }}
                    >
                      {t("global.all")}
                    </Checkbox>
                    <Checkbox.Group
                      style={{ marginLeft: "var(--size-3)" }}
                      options={options}
                      {...field}
                    />
                  </>
                )
              }}
            />
          )}
          {errors.events && (
            <FormControl.HelperText state="error">
              {(errors.events as any).message}
            </FormControl.HelperText>
          )}
        </FormControl>
        <Space>
          <Button
            loading={createPreassignation.isLoading}
            disabled={events.isLoading}
            type="primary"
            htmlType="submit"
          >
            {t("global.create")}
          </Button>
          <Button onClick={props.onClose}>{t("global.cancel")}</Button>
        </Space>
      </form>
    </Drawer>
  )
}

export default CreatePreassignationDrawer
